import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { KeyboardEvent } from "react";
import type { ProposalActivityCoverage } from "work/proposals/negotiation/proposal-activity";
import ProposalCarriedOverFieldHistory from "work/proposals/negotiation/proposal-carried-over-field-history";
import {
  ProposalCarriedOverNegotiationContext,
  type ProposalCarriedOverNegotiation,
} from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import ProposalTimelinePanel from "work/proposals/negotiation/proposal-timeline-panel";

/** The caption of the header button that opens the popover, and the popover's heading. */
export const proposalActivityTitle = "Activity";

const ActivityContainer = styled(Box)(({ theme }) => ({
  maxHeight: 480,
  maxWidth: 440,
  minWidth: 300,
  overflowY: "auto",
  padding: theme.spacing(1.5),
}));

const ActivityHeading = styled(Typography)(({ theme }) => ({
  display: "block",
  paddingBottom: theme.spacing(1),
})) as typeof Typography;

type ProposalActivityPopoverProps = {
  /** The header button. The popover opens below it, and a click on it is not a click away. */
  anchorEl: HTMLElement | null;
  onClose: () => void;
  negotiationId?: string;
  viewerScope?: string;
  /** What the page's field controls already show; see `proposal-activity.ts`. */
  coverage: ProposalActivityCoverage;
  /**
   * The negotiation a revision continues, as the page resolved it. Passed in rather than read from
   * `ProposalCarriedOverNegotiationContext`, because the header sits outside the page content that
   * provides it.
   */
  carriedOver?: ProposalCarriedOverNegotiation;
};

/**
 * The negotiation's activity, from a button in the proposal page header (#5648): who submitted,
 * approved, accepted, rejected, canceled or hired, who changed the reviewers, and the changes no field
 * control on the page shows. It never repeats a field's own history; that stays in the field's popover.
 *
 * On a revision it also shows the activity of the negotiation the revision continues, under "Before
 * this revision", as each field's popover does for that field.
 *
 * A `Popper`, not a `Popover`, for the reason `ProposalFieldHistoryButton` gives: a `Popover` is a
 * `Modal`, and it would take the rest of the page out of the accessibility tree.
 */
export default function ProposalActivityPopover(props: Readonly<ProposalActivityPopoverProps>) {
  const { anchorEl, onClose, negotiationId, viewerScope, coverage, carriedOver } = props;
  const isOpen = Boolean(anchorEl && negotiationId && viewerScope);

  function handleClickAway(event: MouseEvent | TouchEvent) {
    // The header button toggles the popover itself. Closing here as well would reopen it on that click.
    if (anchorEl && event.target instanceof Node && anchorEl.contains(event.target)) return;
    onClose();
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") onClose();
  }

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-end"
      role="presentation"
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper elevation={8} onKeyDown={handleKeyDown}>
          <ActivityContainer data-testid="proposal-activity">
            <ActivityHeading variant="subtitle2" component="h2">
              {proposalActivityTitle}
            </ActivityHeading>
            <ProposalCarriedOverNegotiationContext.Provider value={carriedOver}>
              <ProposalTimelinePanel
                negotiationId={negotiationId}
                viewerScope={viewerScope}
                activity={coverage}
                enabled={isOpen}
                includeComments={false}
                period={carriedOver ? "revision" : undefined}
              />
              <ProposalCarriedOverFieldHistory activity={coverage} enabled={isOpen} />
            </ProposalCarriedOverNegotiationContext.Provider>
          </ActivityContainer>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
